import { t } from "i18next";
import * as z from "zod";

const CURRENCY_RE = /^[A-Z]{3}$/;

/**
 * Schéma du formulaire de comptabilisation manuelle d'une écriture
 * (`POST /JournalEntries/post`, guide §6.4). Les montants sont saisis en
 * unité MAJEURE ; la conversion vers les entiers `amounts` (unité mineure,
 * clés AmountBasis) est faite au submit via `majorToMinor`.
 *
 * Factory (règle projet) : créé via `createPostJournalEntrySchema()` et
 * mémoïsé au niveau composant sur la langue courante — jamais d'instance
 * exportée.
 */
export const createPostJournalEntrySchema = () =>
  z.object({
    productCode: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    providerCode: z.string().trim().or(z.literal("")).optional(),
    sourceService: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    sourceTransactionRef: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    currency: z
      .string()
      .trim()
      .regex(CURRENCY_RE, { message: t("invalidFieldError") }),
    businessDate: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    /** Montant principal (unité MAJEURE — converti en minor au submit). */
    principal: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") }),
    /** Frais (unité MAJEURE, optionnel). */
    fee: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") })
      .optional(),
    /** Taxe (unité MAJEURE, optionnel). */
    tax: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") })
      .optional(),
    /** Net (unité MAJEURE, optionnel). */
    net: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") })
      .optional(),
    /** Commission agent (unité MAJEURE, optionnel). */
    agentCommission: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .min(0, { message: t("invalidFieldError") })
      .optional(),
  });

export type PostJournalEntryFormValues = z.infer<
  ReturnType<typeof createPostJournalEntrySchema>
>;
